import { createContext, useContext, useEffect, useState } from 'react';
import { onSnapshot, query, collection, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { logger } from '../../shared/utils/logger';
import { mapFirestorePostToUIPost } from '../../shared/utils/posts/mapFirestorePostToUIPost';

const PostsContext = createContext();

export function PostsProvider({ children }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const now = new Date();
    
    const q = query(
      collection(db, 'posts'),
      where('activity.date', '>', now),
      // Sort posts by activity date in ascending order to ensure a chronological arrangement of future posts.
      orderBy('activity.date', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const changes = snapshot.docChanges();
      
      setPosts(prev => {
        const updated = [...prev];
        
        changes.forEach(change => {
          const post = mapFirestorePostToUIPost(change.doc.data(), change.doc.id);

          const index = updated.findIndex(p => p.id === post.id);

          if (change.type === 'added') {
            if (index === -1) {
              const insertIndex = updated.findIndex(p => 
                p.activity?.date > post.activity?.date
              );
              if (insertIndex === -1) {
                updated.push(post);
              } else {
                updated.splice(insertIndex, 0, post);
              }
            }
          } else if (change.type === 'modified' && index !== -1) {
            updated[index] = post;
            updated.sort((a, b) => 
              (a.activity?.date || 0) - (b.activity?.date || 0)
            );
          } else if (change.type === 'removed' && index !== -1) {
            updated.splice(index, 1);
          }
        });

        return updated;
      });

      setLoading(false);
    }, error => {
      logger.error('Error listening to posts:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <PostsContext.Provider value={{ posts, loading }}>
      {children}
    </PostsContext.Provider>
  );
}

export const usePosts = () => useContext(PostsContext);
